import { gql } from '@apollo/client';
import {
  GetProjectsParams,
  IProject,
  ITransferredProject,
  IProjectSpaceTypeCategory,
  ProjectType,
} from 'spw-redux/modules/projects/types';
import { IUser } from 'spw-redux/modules/user/types';
import { SimplifiedUserSortOptions } from 'spw-redux/modules/users/types';
import { PROJECT_FIELDS } from 'spw-api/project_fragments';
import { SpaceTypeColor } from 'spw-redux/modules/space_types/types';
import { graphqlClient } from './utils';

export const updateProjectSpaceTypeCategory = (
  projectSpaceTypeCategory: Partial<IProjectSpaceTypeCategory>
) => {
  return graphqlClient.mutate({
    mutation: gql`
      mutation UpdateProjectSpaceTypeCategory(
        $projectSpaceTypeCategory: ProjectSpaceTypeCategoryInput!
      ) {
        projectSpaceTypeCategory: updateProjectSpaceTypeCategory(
          projectSpaceTypeCategory: $projectSpaceTypeCategory
        ) {
          id
          name
          icon
          parentId
          defaultSpaceTypeCategory {
            id
            name
            category
          }
          subcategories {
            id
            name
            parentId
            includeInTotalWorkSeats
            defaultSpaceTypeCategory {
              id
              name
              category
            }
          }
        }
      }
    `,
    variables: {
      projectSpaceTypeCategory,
    },
  });
};

export const getESASpaceTypeCategoryUsage = (id: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetESASpaceTypeCategoryUsage($id: ID!) {
        usage: getESASpaceTypeCategoryUsage(id: $id) {
          spaceTypeCountForPstc
          ... on EsaSpaceTypeCategoryUsage {
            floorplanCountForPstc
          }
        }
      }
    `,
    variables: { id },
  });

export const getProjectSpaceTypeCategoryUsage = (
  id: IProjectSpaceTypeCategory['id']
) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjectSpaceTypeCategoryUsage {
          usage: getProjectSpaceTypeCategoryUsage(project_space_type_category_id: ${id}) {
              space_type_count_for_pstc
              ...on ProgrammingSpaceTypeCategoryUsage {
                  worksheet_count_for_pstc
              }
          }
      }`,
  });

export const deleteCustomSpaceTypeCategories = (
  projectId: number,
  ids: number[]
) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteCustomSpaceTypeCategories($projectId: ID!) {
          deleteCustomSpaceTypeCategories(project_id: $projectId, ids: ${ids}) {
              numberOfCategoriesDeleted
          }
      }
  `,
    variables: {
      projectId,
    },
  });

export const getProjectInfoById = (projectId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      ${PROJECT_FIELDS}
      query GetProject {
          project(projectId: ${projectId}) {
              ...ProjectFields
          }
      }`,
  });

export const getProjects = (params: GetProjectsParams) => {
  const sort = { ...params?.sort };

  if (sort.isMarkedAsFavoriteByUser) {
    sort.projectName = 'ASC';
  }

  return graphqlClient.query<{
    projects: { projects: IProject[]; total: number };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjects(
        $type: String
        $teamId: Int
        $clientId: Int
        $clientIds: [Int]
        $industryId: Int
        $industryIds: [Int]
        $locations: [LocationInput]
        $status: String
        $isHeadquarters: Boolean
        $minProjectStartYear: Int
        $maxProjectStartYear: Int
        $minClientMoveInYear: Int
        $maxClientMoveInYear: Int
        $minProjectUSF: Int
        $maxProjectUSF: Int
        $perPage: Int
        $page: Int
        $sort: ProjectSortOptions
        $search: String
      ) {
        projects(
          type: $type
          perPage: $perPage
          page: $page
          status: $status
          clientId: $clientId
          clientIds: $clientIds
          industryId: $industryId
          industryIds: $industryIds
          minProjectStartYear: $minProjectStartYear
          maxProjectStartYear: $maxProjectStartYear
          minClientMoveInYear: $minClientMoveInYear
          maxClientMoveInYear: $maxClientMoveInYear
          minProjectUSF: $minProjectUSF
          maxProjectUSF: $maxProjectUSF
          locations: $locations
          isHeadquarters: $isHeadquarters
          teamId: $teamId
          sort: $sort
          search: $search
        ) {
          projects {
            id
            isMember
            insertedAt
            updatedAt
            client {
              name
              id
              industry {
                name
              }
            }
            city
            state
            countryCode
            projectName
            projectNumber
            requiresRequestToJoin
            isMarkedAsFavoriteByUser
            hasMissingBenchmarkData
            status
            year
            lastEditor {
              id
              name
              isSuperAdmin
            }
            createdByUser {
              id
              name
              email
            }
            projectOwner {
              id
              name
              email
              isActive
            }
            type
            buildings {
              id
              city
              state
              countryCode
              streetAddress
              squareFeet
              programmingUsf
              floors {
                id
                name
              }
            }
            spaceTypeCategories {
              id
              name
              parentId
              defaultSpaceTypeCategory {
                id
                category
              }
              subcategories {
                id
                name
                parentId
                includeInTotalWorkSeats
                defaultSpaceTypeCategory {
                  id
                  category
                }
              }
            }
          }
          total
        }
      }
    `,
    variables: { ...params, sort },
  });
};

export const joinProject = (id: number) =>
  graphqlClient.query<{
    projectEditor: { userId: number; projectId: number };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation JoinProject($projectId: Int!) {
        projectEditor: joinProject(projectId: $projectId) {
          userId
          projectId
        }
      }
    `,
    variables: { projectId: id },
  });

export const issueRequestToJoinProject = (id: number) =>
  graphqlClient.query<{ issueRequestToJoinProject: { id: number } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation IssueRequestToJoinProject($projectId: Int!) {
        issueRequestToJoinProject: issueRequestToJoinProject(
          projectId: $projectId
        ) {
          id
        }
      }
    `,
    variables: { projectId: id },
  });

export const getProjectEditors = (params: {
  projectId: number;
  page?: number;
  perPage?: number;
  sort?: SimplifiedUserSortOptions;
}) =>
  graphqlClient.query<{ results: { users: IUser[]; total: number } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjectEditors(
        $projectId: ID!
        $page: Int
        $perPage: Int
        $sort: SimplifiedUserSortOptions
      ) {
        results: projectEditors(
          projectId: $projectId
          page: $page
          perPage: $perPage
          sort: $sort
        ) {
          total
          users {
            id
            name
            officeLocation
            professionalRole
            numberOfProjects
            numberOfTeams
            hasAvatar
            thumbnailS3Path
          }
        }
      }
    `,
    /*
adding default a items per page to make sure we disable all the editors in the add project
editors dialog, otherwise we would have to create a query that returns the users that
match a search text with a disabled flag in case they're already added to the project
*/
    variables: { ...params, perPage: params.perPage || 999 },
  });

export const removeProjectEditor = (params: {
  userId: number;
  projectId: number;
}) =>
  graphqlClient.query<{ userId: number }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation RemoveProjectEditor($userId: ID!, $projectId: ID!) {
        userId: removeEditorUserFromProject(
          editorId: $userId
          projectId: $projectId
        ) {
          userId
        }
      }
    `,
    variables: params,
  });

export const addTeamToProject = (teamId: number, projectId: number) => {
  return graphqlClient.query<{ addedTeam: { id: number; teamId: number } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation AddTeamToProject($teamId: ID!, $projectId: ID!) {
        addedTeam: addEditingTeamToProject(
          teamId: $teamId
          projectId: $projectId
        ) {
          teamId
        }
      }
    `,
    variables: { teamId, projectId },
  });
};

export const removeTeamFromProject = (teamId: number, projectId: number) => {
  return graphqlClient.query<{ removedTeam: { id: number; teamId: number } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation RemoveTeamFromProject($teamId: ID!, $projectId: ID!) {
        removedTeam: removeEditingTeamFromProject(
          teamId: $teamId
          projectId: $projectId
        ) {
          teamId
        }
      }
    `,
    variables: { teamId, projectId },
  });
};

export const recordProjectActivity = (projectId: number) => {
  return graphqlClient.query<{ success: boolean }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation RecordProjectActivity($projectId: Int!) {
        success: recordProjectActivity(projectId: $projectId)
      }
    `,
    variables: {
      projectId,
    },
  });
};

export const addProjectEditor = (params: {
  userId: number;
  projectId: number;
}) =>
  graphqlClient.query<{ userId: number }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation AddProjectEditor($userId: ID!, $projectId: ID!) {
        userId: addEditorUserToProject(userId: $userId, projectId: $projectId) {
          userId
        }
      }
    `,
    variables: params,
  });

export const transferProjectOwnership = (params: {
  userId: number;
  projectId: number;
  preservePreviousOwnerAsEditor: boolean;
}) => {
  return graphqlClient.query<{ project: ITransferredProject }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation TransferProjectOwnership(
        $projectId: Int!
        $userId: Int!
        $preservePreviousOwnerAsEditor: Boolean
      ) {
        project: transferProject(
          userId: $userId
          projectId: $projectId
          preservePreviousOwnerAsEditor: $preservePreviousOwnerAsEditor
        ) {
          id
          projectOwnerId
        }
      }
    `,
    variables: params,
  });
};

export const leaveProject = (params: { projectId: number }) =>
  graphqlClient.query<{ success: boolean }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation LeaveProject($projectId: Int!) {
        projectEditor: leaveProject(projectId: $projectId) {
          userId
          projectId
        }
      }
    `,
    variables: { projectId: params.projectId },
  });

// Use this query to check if a projects exists either by project name or project number
// It's meant to be used with a single param (project name / project number) per request
export const checkProjectExistenceByParams = (params: {
  projectName?: string;
  projectNumber?: string;
  projectType?: ProjectType;
}) =>
  graphqlClient.query<{ projectExists: boolean }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query CheckProjectExistenceByParams(
        $projectName: String
        $projectNumber: String
        $projectType: String
      ) {
        projectExists: doesProjectWithParamsExist(
          projectName: $projectName
          projectNumber: $projectNumber
          projectType: $projectType
        )
      }
    `,
    variables: params,
  });

export const getProjectNameSuggestionForClone = (projectName: string) =>
  graphqlClient.query<{ projectNameSuggestion: string }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjectNameSuggestionForClone($projectName: String!) {
        projectNameSuggestion: getProjectNameSuggestionForClone(
          projectName: $projectName
        )
      }
    `,
    variables: { projectName },
  });

export const getProjectSpaceTypes = (projectId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjectSpaceTypes{
          spaceTypes: projectSpaceTypes(projectId: ${projectId}) {
              id
              icon
              previousId
              projectId
              clientFacingName
              isCustom
              color
              gradientColorIndex
              spaceType {
                  id
                  spaceType
                  name
                  isCustom
                  icon
              }
              category {
                  id
                  name
                  includeInTotalWorkSeats
                  parentId
                  defaultSpaceTypeCategory {
                      id
                      category
                      name
                  }
              }
          }
      }
  `,
  });

export const updateSelectedProjectColorsApi = (
  projectId: number,
  selectedProjectColors: {
    subcategoryColors: { id: number; color: string }[];
    spaceTypeColors: SpaceTypeColor[];
  }
) =>
  graphqlClient.mutate<{
    updatedProjectColors: {
      subcategoryColors: {
        id: number;
        color: string;
        gradientColorIndex: number;
      }[];
      spaceTypeColors: SpaceTypeColor[];
    };
  }>({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation UpdateSelectedProjectColors(
        $projectId: ID!
        $selectedProjectColors: SelectedProjectColorArgs!
      ) {
        updatedProjectColors: updateSelectedProjectColors(
          projectId: $projectId
          selectedProjectColors: $selectedProjectColors
        ) {
          subcategoryColors {
            id
            color
            gradientColorIndex
          }
          spaceTypeColors {
            id
            color
            gradientColorIndex
          }
        }
      }
    `,
    variables: {
      projectId,
      selectedProjectColors,
    },
  });
